import { CheckCircle2, XCircle, Clock } from 'lucide-react'
import { Button } from '../ui/button'
import { useI18n } from '../../i18n'
import type { PayStatus } from '../../types'

interface PaymentResultProps {
  status: PayStatus
  onRetry: () => void
  onClose: () => void
}

export function PaymentResult({ status, onRetry, onClose }: PaymentResultProps) {
  const { t } = useI18n()

  const configs: Record<string, { icon: React.ElementType; color: string; titleZh: string; titleEn: string; descZh: string; descEn: string }> = {
    PAID: {
      icon: CheckCircle2,
      color: 'text-emerald-500',
      titleZh: '支付成功',
      titleEn: 'Payment Successful',
      descZh: '套餐已生效，祝您使用愉快！',
      descEn: 'Your plan is now active. Enjoy!',
    },
    FAILED: {
      icon: XCircle,
      color: 'text-red-500',
      titleZh: '支付失败',
      titleEn: 'Payment Failed',
      descZh: '支付过程中出现问题，请重试。',
      descEn: 'Something went wrong. Please try again.',
    },
    CLOSED: {
      icon: Clock,
      color: 'text-gray-400',
      titleZh: '订单已关闭',
      titleEn: 'Order Closed',
      descZh: '支付超时，订单已关闭，请重新发起支付。',
      descEn: 'Payment timed out. Please try again.',
    },
    REFUNDED: {
      icon: XCircle,
      color: 'text-amber-500',
      titleZh: '已退款',
      titleEn: 'Refunded',
      descZh: '订单已退款。',
      descEn: 'The order has been refunded.',
    },
  }

  const config = configs[status] || configs.FAILED
  const Icon = config.icon

  return (
    <div className="flex flex-col items-center py-8">
      <Icon className={`h-16 w-16 ${config.color} mb-4`} />
      <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2">
        {t(config.titleZh, config.titleEn)}
      </h3>
      <p className="text-sm text-gray-400 mb-6 text-center max-w-xs">
        {t(config.descZh, config.descEn)}
      </p>

      <div className="flex gap-3">
        {status === 'PAID' ? (
          <Button onClick={onClose} className="bg-orange-500 hover:bg-orange-600 text-white h-10 text-sm font-medium rounded-xl px-8">
            {t('开始使用', 'Get Started')}
          </Button>
        ) : (
          <>
            <Button onClick={onRetry} className="bg-orange-500 hover:bg-orange-600 text-white h-10 text-sm font-medium rounded-xl px-6">
              {t('重新支付', 'Retry Payment')}
            </Button>
            <Button onClick={onClose} variant="outline" className="h-10 text-sm font-medium rounded-xl px-6">
              {t('关闭', 'Close')}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
